import {
  ArrowLeft,
  ArrowUpRight,
  Banknote,
  BanknoteArrowDown,
  BanknoteArrowUp,
  Bell,
  Building2,
  Calculator,
  Calendar,
  CalendarCheck,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleCheck,
  CircleX,
  CreditCard,
  Crop,
  Download,
  Droplet,
  EllipsisVertical,
  Eye,
  EyeOff,
  File,
  FileBarChart,
  FileCode,
  FileSpreadsheet,
  FileText,
  Flame,
  Fuel,
  Fingerprint,
  Folder,
  FolderCog,
  FolderOpen,
  Funnel,
  GitMerge,
  Grid2x2,
  HandCoins,
  Hash,
  History,
  Home,
  Info,
  KeyRound,
  Languages,
  Layers3,
  Link2,
  LoaderCircle,
  LogOut,
  MapPin,
  Minus,
  Moon,
  Network,
  NotebookPen,
  Package,
  PackageOpen,
  Pencil,
  Plus,
  Printer,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Save,
  Search,
  SearchX,
  Send,
  ShieldCheck,
  Sun,
  Trash2,
  TriangleAlert,
  Truck,
  Upload,
  User,
  UsersRound,
  Vault,
  Wifi,
  WifiOff,
  X,
  ZoomIn,
  ZoomOut,
  type LucideProps,
} from 'lucide-react';
import { createElement, type ComponentType } from 'react';
import { cn } from '@/lib/utils';

/**
 * The single icon source for the application (rule I1). Product code imports
 * from here and never from `lucide-react`, so one action always carries one
 * glyph and a swap happens in a single file.
 *
 * Names are the canonical lucide names of the installed version (rule I2).
 * Several entries in the icon table are spelled with names that lucide has
 * since retired into aliases; the canonical spelling is used instead and the
 * glyph is unchanged:
 *
 * | Icon table | Canonical name in lucide-react 1.x |
 * | ---------- | ---------------------------------- |
 * | `Filter`        | `Funnel`           |
 * | `MoreVertical`  | `EllipsisVertical` |
 * | `Loader2`       | `LoaderCircle`     |
 * | `CheckCircle2`  | `CircleCheck`      |
 * | `XCircle`       | `CircleX`          |
 * | `AlertTriangle` | `TriangleAlert`    |
 */

export type IconComponent = ComponentType<LucideProps>;

/** Rule I5. Pixel sizes, for callers that size an icon numerically. */
export const ICON_SIZE = {
  /** Inline and action icons. */
  inline: 16,
  /** Overflow menus and status icons. */
  status: 20,
  /** Empty states and dialog semantics. */
  display: 24,
} as const;

/** Rule I5 as Tailwind utilities, which is how components usually size icons. */
export const ICON_SIZE_CLASS = {
  inline: 'size-4',
  status: 'size-5',
  display: 'size-6',
} as const;

/**
 * Rule R4. The only place in the application that mirrors an icon for RTL.
 * It is a CSS variant rather than a direction lookup, so it also holds inside
 * portalled content that never re-renders on a locale change.
 */
export const MIRRORED_ICON_CLASS = 'rtl:-scale-x-100';

function decorate(Icon: IconComponent, extraClassName: string, displayName: string): IconComponent {
  function DecoratedIcon({ className, ...props }: LucideProps) {
    return createElement(Icon, { ...props, className: cn(extraClassName, className) });
  }
  DecoratedIcon.displayName = displayName;
  return DecoratedIcon;
}

/**
 * Wraps a directional icon so it mirrors with the document direction. Use this
 * instead of attaching an `rtl:` variant at the call site (rule R4). Icons that
 * must never mirror — brand marks, numerals, media controls (rule R5) — are
 * simply not wrapped.
 */
export function mirrorIcon(Icon: IconComponent, displayName: string): IconComponent {
  return decorate(Icon, MIRRORED_ICON_CLASS, displayName);
}

/** Create / add a business entity. */
export const IconCreate: IconComponent = Plus;

/** The − of a quantity stepper. */
export const IconDecrease: IconComponent = Minus;
/** Edit an existing record. */
export const IconEdit: IconComponent = Pencil;
/** Delete a record. */
export const IconDelete: IconComponent = Trash2;
/** Save changes. */
export const IconSave: IconComponent = Save;
/** Confirm / submit a decision. */
export const IconConfirm: IconComponent = Check;
/** Cancel an action, and close a surface. */
export const IconCancel: IconComponent = X;
/** Search. */
export const IconSearch: IconComponent = Search;
/** Open or apply filters. */
export const IconFilter: IconComponent = Funnel;
/** Clear every active filter. */
export const IconResetFilters: IconComponent = RotateCcw;
/** Row and toolbar overflow menu. */
export const IconOverflow: IconComponent = EllipsisVertical;
/** View the details of a record. */
export const IconViewDetails: IconComponent = Eye;
/** Show or hide a password. */
export const IconShowPassword: IconComponent = Eye;
export const IconHidePassword: IconComponent = EyeOff;
/** Print. */
export const IconPrint: IconComponent = Printer;
/** Export to a spreadsheet. */
export const IconExportSpreadsheet: IconComponent = FileSpreadsheet;
/** Download a file. */
export const IconDownload: IconComponent = Download;
/** Refresh, or change state. */
export const IconRefresh: IconComponent = RefreshCw;
/** Send / post. */
export const IconSend: IconComponent = Send;
/** Navigate back. Mirrors with the direction. */
export const IconBack: IconComponent = mirrorIcon(ArrowLeft, 'IconBack');
/** Navigate to a related record. Mirrors with the direction. */
export const IconNavigateToRecord: IconComponent = mirrorIcon(ArrowUpRight, 'IconNavigateToRecord');
/** Busy. Carries its own animation so no caller has to remember it. */
export const IconBusy: IconComponent = decorate(LoaderCircle, 'animate-spin', 'IconBusy');
/** Success status. */
export const IconSuccess: IconComponent = CircleCheck;
/** Error status. */
export const IconError: IconComponent = CircleX;
/** Warning status. */
export const IconWarning: IconComponent = TriangleAlert;
/** Information status. */
export const IconInfo: IconComponent = Info;

/**
 * Additional icons for navigation, domain concepts and lifecycle actions that
 * appear in product code but are not one of the 23 actions catalogued above
 * (rule I3 only names those). They still route through this module, so rule
 * I1 continues to hold — lucide-react is imported nowhere else in product
 * code. None of these belong in `ICONS`/`ICON_NAMES`, which mirror the
 * UI/UX principles table exactly and nothing more.
 */
/** Language switch. */
export const IconLanguage: IconComponent = Languages;
/** Switch to the dark theme. */
export const IconThemeDark: IconComponent = Moon;
/** Switch to the light theme. */
export const IconThemeLight: IconComponent = Sun;
/** Identity: review center, identity-conflict cases. */
export const IconIdentity: IconComponent = Fingerprint;
/** Information-completeness and reliability classification. */
export const IconClassification: IconComponent = Layers3;
/** A connected integration source. */
export const IconIntegration: IconComponent = Network;
/** The Unified Access application launcher (الأنظمة). */
export const IconApplications: IconComponent = Grid2x2;
/** The session branch, and changing it (تغيير الفرع). */
export const IconBranch: IconComponent = Building2;
/** Permission groups and access control. */
export const IconAccess: IconComponent = ShieldCheck;
/** The customer directory. */
export const IconCustomers: IconComponent = UsersRound;
/** Expands a trigger's attached menu. */
export const IconMenuExpand: IconComponent = ChevronsUpDown;
/** Sign out. */
export const IconLogout: IconComponent = LogOut;
/** Generic user / account avatar placeholder. */
export const IconUser: IconComponent = User;
/** A branch or physical location. */
export const IconLocation: IconComponent = MapPin;
/** Merge two records. */
export const IconMerge: IconComponent = GitMerge;
/** An audit or reliability history entry. */
export const IconHistory: IconComponent = History;
/** An integration source's API credential. */
export const IconApiKey: IconComponent = KeyRound;
/** A linked or associated record. */
export const IconLink: IconComponent = Link2;
/** No results found. */
export const IconNoResults: IconComponent = SearchX;
/**
 * Restore a previously removed record. Shares its glyph with the
 * catalogued reset-filters action (both read as "revert"), but names a
 * distinct lifecycle action; reset-filters has no current call site.
 */
export const IconRestore: IconComponent = RotateCcw;
/** Previous page. Mirrors with the direction. */
export const IconPreviousPage: IconComponent = mirrorIcon(ChevronLeft, 'IconPreviousPage');
/** Next page. Mirrors with the direction. */
export const IconNextPage: IconComponent = mirrorIcon(ChevronRight, 'IconNextPage');
/** The operator app itself: العمليات, its home page and navigation entry. */
export const IconFuelOperations: IconComponent = Fuel;
/** Dashboard / home navigation link. */
export const IconHome: IconComponent = Home;
/** Unread notifications. */
export const IconNotifications: IconComponent = Bell;
/** A date field or a date-bearing record. */
export const IconDate: IconComponent = Calendar;
/**
 * Indicates a collapsible section, or a custom dropdown/select trigger, is
 * open or closed. The caller rotates it with a CSS transform keyed on state.
 * Unlike IconPreviousPage/IconNextPage this is never mirrored for RTL: "open
 * downward" reads the same in both directions.
 */
export const IconExpandToggle: IconComponent = ChevronDown;
/**
 * A collapsed tree node that can be expanded, pointing toward the content it
 * reveals. Unlike IconExpandToggle's in-place rotation, this swaps glyph by
 * direction the same way IconNextPage does, so it mirrors per rule R4 rather
 * than a hand-attached RTL branch choosing between ChevronLeft/ChevronRight.
 */
export const IconTreeExpand: IconComponent = mirrorIcon(ChevronRight, 'IconTreeExpand');
/** The server can be reached (PDA offline mode, fuel-pda.md 13.3). */
export const IconOnline: IconComponent = Wifi;
/** The server cannot be reached: work is saved on the device. */
export const IconOffline: IconComponent = WifiOff;
/** Upload a file. */
export const IconUpload: IconComponent = Upload;
/** Capture a photo with the device camera. */
export const IconCamera: IconComponent = Camera;
/** Crop an image. */
export const IconCrop: IconComponent = Crop;
/** Rotate an image clockwise. */
export const IconRotate: IconComponent = RotateCw;
/** Zoom in on an image. */
export const IconZoomIn: IconComponent = ZoomIn;
/** Zoom out on an image. */
export const IconZoomOut: IconComponent = ZoomOut;
/** A physical card presented to a card reader (e.g. NFC sign-in). */
export const IconCard: IconComponent = CreditCard;
/** A product record. */
export const IconProduct: IconComponent = Package;
/** A product's unit of measure, labeling a unit selection field. */
export const IconProductUnit: IconComponent = Hash;
/**
 * A product-unit search result, distinct from a plain product result.
 * Deliberately not `Layers3`: in the installed lucide-react version it
 * resolves to the identical glyph file as `Layers3` (already used above for
 * `IconClassification`), which would make the two indistinguishable.
 */
export const IconUnitVariant: IconComponent = PackageOpen;
/** A folder in a file/tree browser. */
export const IconFolder: IconComponent = Folder;
/** An open folder in a file/tree browser. */
export const IconFolderOpen: IconComponent = FolderOpen;
/** A folder holding configuration. */
export const IconSettingsFolder: IconComponent = FolderCog;
/** A generic file in a file/tree browser. */
export const IconFile: IconComponent = File;
/** A report or chart-bearing file. */
export const IconReportFile: IconComponent = FileBarChart;
/** A text document file. */
export const IconDocumentFile: IconComponent = FileText;
/** A source or config file. */
export const IconCodeFile: IconComponent = FileCode;
/**
 * Marks a sortable table column with no active sort applied yet (section 8,
 * rule L8). Shares its glyph with `IconMenuExpand` — both read as "this
 * control has more than one state to cycle through" — but names a distinct
 * affordance, so it is exported separately rather than reused under that
 * name. The active-sort direction itself is shown by rotating
 * `IconExpandToggle`, not a second icon, and is carried primarily by the
 * column header's `aria-sort` attribute rather than the glyph (rule I6).
 */
export const IconSortable: IconComponent = ChevronsUpDown;

/**
 * A sidebar group's disclosure chevron (section 41, rule NV8). The call site
 * wraps the trigger's `Collapsible` root with `className="group/collapsible"`
 * so this icon can react to that group's own `data-state` via Tailwind's
 * `group-data-.../collapsible:` variant — no `isOpen` prop needed here.
 *
 * While the group is closed it points toward the inline-end edge, mirrored
 * through the shared `rtl:` convention (rule R4) rather than a hand-attached
 * direction check. Once open it rotates to point straight down, in both
 * directions — the mirror is scoped to the closed state only, because
 * combining it with the open rotation would point the glyph up instead of
 * down in Arabic. That combination is exactly the defect section 17 records
 * in the reference sidebar: `ChevronRight` rotated a fixed 90° with no
 * regard for the mirror, so in Arabic the affordance points away from what
 * it opens. Positioning (`ms-auto`) is the call site's job, same as any
 * other icon; this component only ever carries logical classes.
 */
export const IconNavDisclosure: IconComponent = decorate(
  ChevronRight,
  'transition-transform group-data-[state=closed]/collapsible:rtl:-scale-x-100 group-data-[state=open]/collapsible:rotate-90',
  'IconNavDisclosure',
);

/*
 * Fuel operator operations (fuel.md section 4). One glyph per home-page
 * operation, reused in its drawer header so the operator recognises the task
 * they opened.
 */
/** 1. الرصيد المدور: the carried cash balance. */
export const IconRevolvingBalance: IconComponent = Vault;
/** 2. الشراء: a fuel delivery bought in. */
export const IconPurchase: IconComponent = Truck;
/** 3. البيع النقدي: a cash sale at the pump. */
export const IconCashSale: IconComponent = Banknote;
/** 4. بيع الذمم: a sale charged to a debtor's account. */
export const IconDebtSale: IconComponent = NotebookPen;
/** 5. سداد الذمم: a debtor paying their account back. */
export const IconDebtRepayment: IconComponent = HandCoins;
/** 6. المدفوعات: cash paid out. */
export const IconPayment: IconComponent = BanknoteArrowUp;
/** 7. المقبوضات: cash received. */
export const IconCashReceipt: IconComponent = BanknoteArrowDown;
/** 4.1 إغلاق الشفت: close the business day. */
export const IconShiftClose: IconComponent = CalendarCheck;
/** The diesel product (محروقات). */
export const IconDiesel: IconComponent = Droplet;
/** The petrol product (بنزين). */
export const IconPetrol: IconComponent = Flame;
/** A live computed summary (ملخص العملية). */
export const IconSummary: IconComponent = Calculator;

/** The icon table of the UI/UX principles, in the order it is written there. */
export const ICONS = {
  create: IconCreate,
  edit: IconEdit,
  delete: IconDelete,
  save: IconSave,
  confirm: IconConfirm,
  cancel: IconCancel,
  search: IconSearch,
  filter: IconFilter,
  resetFilters: IconResetFilters,
  overflow: IconOverflow,
  viewDetails: IconViewDetails,
  print: IconPrint,
  exportSpreadsheet: IconExportSpreadsheet,
  download: IconDownload,
  refresh: IconRefresh,
  send: IconSend,
  back: IconBack,
  navigateToRecord: IconNavigateToRecord,
  busy: IconBusy,
  success: IconSuccess,
  error: IconError,
  warning: IconWarning,
  info: IconInfo,
} as const satisfies Record<string, IconComponent>;

export type IconName = keyof typeof ICONS;

/** Every semantic action the icon table covers, in table order. */
export const ICON_NAMES = Object.keys(ICONS) as readonly IconName[];
